import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import differenceInDays from 'date-fns/differenceInDays';

import get from 'lodash.get';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import Card from 'components/CncUI/Card';
import PlaidInfoPanel from './Plaid/InfoPanel';
import PlaidEditForm from './Plaid/EditForm';
import ComplexPanel from './ComplexPanel';
import CopyModal from './CopyModal';
import EditAction from '../Shared/EditAction';

import useCountry from 'hooks/useCountry';
import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import useSnackbar from 'hooks/useSnackbar';
import FmsEnums from 'constants/componentData/fmsEnums';
import support from 'constants/config/support';
import isComplex from './utils/isComplex';
import getStorage from 'utilities/getStorage';

const useStyles = makeStyles(({ breakpoints, palette }) => ({
  button: {
    padding: '2px 4px',
    color: palette.cncUI.default['+10'],
    '&:hover': {
      color: palette.cncUI.default['+10'],
    },
    '&:focus': {
      color: palette.cncUI.default['+10'],
    },
    '&:active': {
      color: palette.cncUI.default['+10'],
    },
  },
  content: {
    padding: '40px 24px 56px 24px !important',
  },
}));

const DirectDeposit = props => {
  const classes = useStyles();
  const notify = useSnackbar();
  const storage = getStorage();
  const { country } = useCountry();
  const [edit, setEdit] = useState(false);
  const [mappings, setMappings] = useState({});
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayCopyModal, setCopyModal] = useState(false);

  const { oAuthState, currentLoanout = {} } = props;
  const { basicInfo: info } = profile || {};
  const originalDirectDeposits = get(profile, 'directDeposits', []) || [];
  const directDeposits = originalDirectDeposits.filter(
    dd => (dd.forCorps || '').toLowerCase() === 'c',
  );
  let updateDirectDeposits = directDeposits;
  const [enabled, setEnabled] = useState(
    directDeposits?.length > 0 || Boolean(oAuthState),
  );
  const supportInfo = support.helpDesk;
  const isComplexDDSetup = get(profile, 'basicInfo.isComplexDDSetup', false);
  const complex = isComplexDDSetup || isComplex(directDeposits);
  const activeLoanOutDD = JSON.parse(
    storage.getItem('activeLoanOutDD') || '{}',
  );

  const getMyProfiles = useQuery('GetMyProfiles', {
    // TODO Add an error message for FMS timeouts
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    scopes: 'payroll_employee:edit',
    request: country => ({ country }),
    response: res => ({
      user: res.data.getCurrentUser,
      mappings: res.data.getProfileMappingOptions,
    }),
  });

  const updateProfile = useMutation('updateFullEmployeeProfile', {
    scopes: 'payroll_employee:edit',
    request: input => ({ input: { countryFlag: country, ...input } }),
    response: res => res.data.updateFullEmployeeProfile,
  });

  const onEdit = () => setEdit(true);
  const cancel = () => {
    setEdit(false);
    updateDirectDeposits =
      get(profile, 'directDeposits', []).filter(
        dd => (dd.forCorps || '').toLowerCase() === 'c',
      ) || [];
    setEnabled(directDeposits?.length > 0);
  };
  const startSetup = () => {
    setCopyModal(false);
    setEnabled(true);
    setEdit(true);
  };
  const cancelSetup = () => {
    storage.setItem(
      'activeLoanOutDD',
      JSON.stringify({
        reminderDate: new Date(),
        activeLoanOut: currentLoanout.taxId,
      }),
    );
    setCopyModal(false);
  };

  useEffect(() => {
    setLoading(true);
    getMyProfiles(country)
      .then(response => {
        setUser(response.user);
        setMappings(response.mappings);
      })
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, [country]);

  useEffect(() => {
    if (user?.profiles) {
      const { caps, cnc } = FmsEnums.dataSource;
      const dataSource = type => p => p.dataSource === type;
      let userProfile = user.profiles.find(dataSource(cnc));
      if (!userProfile) {
        userProfile = user.profiles.find(dataSource(caps));
      }
      if (userProfile) {
        setProfile(userProfile);
      } else {
        // sets the profile with the 1st profile in response
        setProfile(get(user, 'profiles[0]', null));
      }
    }
  }, [user]);

  useEffect(() => {
    setEnabled(directDeposits.length > 0 || Boolean(oAuthState));
    const daysSinceReminder = differenceInDays(
      new Date(),
      new Date(activeLoanOutDD.reminderDate),
    );
    if (
      directDeposits.length === 0 &&
      (!activeLoanOutDD.reminderDate || daysSinceReminder > 30)
    ) {
      setCopyModal(true);
    }
  }, [profile]);

  useEffect(() => {
    if (oAuthState) {
      setEdit(true);
      setTimeout(() => {
        const parent = document.getElementById('Home');
        const header = document.getElementById('HeaderTitle');
        const target = document.getElementById(`Loanouts-DirectDeposit`);
        const top = target.offsetTop - header.offsetTop;
        parent.scroll({ top });
      }, 100);
    }
  }, [oAuthState]);

  const updateUserProfile = async input => {
    notify({
      type: 'info',
      message: 'Updating profile information, please wait...',
      duration: 0,
      style: { marginTop: 120 },
    });

    if (!input.basicInfo) {
      input.basicInfo = {};
    }

    input.basicInfo.idType = profile.basicInfo.idType;

    return updateProfile(input)
      .then(() => {
        notify({
          type: 'success',
          message: 'Direct Deposits updated successfully',
          duration: 4500,
          style: { marginTop: 120 },
        });
      })
      .catch(() => {
        notify({
          type: 'error',
          message: `There was an error updating your Direct Deposits information.
            Please try again later or contact product support if the issue
            persists`,
          duration: 6000,
          style: { marginTop: 120 },
        });
      })
      .finally(() => {
        setLoading(true);
        getMyProfiles(country)
          .then(response => {
            setUser(response.user);
            setMappings(response.mappings);
          })
          .catch(err => logger.error(err))
          .finally(() => setLoading(false));
      });
  };

  if (loading) return <Loader />;

  return (
    <>
      <Card id="Loanouts-DirectDeposit">
        <Card.Title
          action={<EditAction action={!complex && !edit && onEdit} />}
        >
          Direct Deposit
        </Card.Title>
        <Card.Content className={classes.content}>
          {complex ? (
            <ComplexPanel
              enabled={enabled}
              directDeposits={updateDirectDeposits}
              mappings={mappings}
              support={supportInfo}
            />
          ) : edit ? (
            <PlaidEditForm
              directDeposits={updateDirectDeposits}
              mappings={mappings}
              support={supportInfo}
              enabled={enabled}
              dbCode={info.dbCode}
              setEnabled={setEnabled}
              onCancel={cancel}
              onSave={updateUserProfile}
              oAuthState={oAuthState}
            />
          ) : (
            <ErrorBoundary alert>
              <PlaidInfoPanel
                enabled={enabled}
                directDeposits={updateDirectDeposits}
                mappings={mappings}
                support={supportInfo}
              />
            </ErrorBoundary>
          )}
        </Card.Content>
      </Card>
      {displayCopyModal && (
        <CopyModal
          directDeposits={originalDirectDeposits}
          dbCode={info.dbCode}
          mappings={mappings}
          onSave={updateUserProfile}
          cancelSetup={cancelSetup}
          startSetup={startSetup}
          closeModal={() => setCopyModal(false)}
        />
      )}
    </>
  );
};

export default DirectDeposit;
